import { BUTTON_LABELS, FEATURE_FLAGS } from '@/constants';
import { cn } from '@/utils';
import { ScoreRowCardFeedbackSection } from './subcomponents/ScoreRowCardFeedbackSection';
import { ScoreRowCardProps } from './ScoreRowCard.types';
import { ScoreRowCardScoreDisplay } from './subcomponents/ScoreRowCardScoreDisplay';
import { scoreRowCardStyles } from './ScoreRowCard.styles';
import { ScoreRowCardTitleRowAndSkillTag } from './subcomponents/ScoreRowCardTitleRowAndSkillTag';
import { ScoreRowCardToggleButton } from './subcomponents/ScoreRowCardToggleButton';
import { useAnimatedHeightTransition } from './utils/useAnimatedHeightTransition';
import { useScoreRowCardFeature } from './utils/useScoreRowCard';
import React, { useCallback } from 'react';

/**
 * ScoreRowCard component displays a score with category information,
 * skill level, and expandable feedback content.
 */
export const ScoreRowCard: React.FC<ScoreRowCardProps> = ({
  category,
  className,
  collapseLabel = BUTTON_LABELS.SHOW_LESS,
  expandLabel = BUTTON_LABELS.SHOW_MORE,
  feedback = [],
  forceExpanded = false,
  isLoading,
  onToggle,
  pass,
  score,
  scoreCardId,
  sessionId,
  sharedWithManager,
  showSkillLevelBadge = !FEATURE_FLAGS.ENABLE_SCORECARD_V2,
  showToggle = true,
  simulationType,
  skillLevel,
  type,
  userId,
  ...props
}) => {
  // ===========================
  // Hooks
  // ===========================
  // Feature hook
  const {
    handleToggle: handleFeatureToggle,
    hasMoreFeedback,
    isExpanded,
    scoreCardVariant,
    showSkillLevelTag,
    visibleFeedback,
  } = useScoreRowCardFeature({
    feedback,
    forceExpanded,
    isLoading,
    pass,
    scoreType: score?.type,
    showSkillLevelBadge,
    showToggle,
    simulationType,
    skillLevel,
    type: type === 'violation' ? 'violation' : 'other',
  });

  // Animated height transition hook for feedback section
  const {
    contentRef: feedbackContentRef,
    height: feedbackHeight,
    handleTransitionEnd: handleFeedbackTransitionEnd,
    isAnimating: isAnimatingFeedback,
    prepareForTransition,
  } = useAnimatedHeightTransition({ dependencies: [visibleFeedback] });

  // ===========================
  // Handlers
  // ===========================

  // Handle toggle - update internal state and call parent callback
  const handleToggle = useCallback(() => {
    prepareForTransition(); // Prepare for the height transition before toggling
    handleFeatureToggle(); // Toggle the expanded/collapsed state of the feedback section
    if (onToggle) onToggle(); // Call the parent onToggle callback if provided
  }, [handleFeatureToggle, onToggle, prepareForTransition]);

  // ===========================
  // Render
  // ===========================
  return (
    <div className={cn(scoreRowCardStyles.container, className)} {...props}>
      <ScoreRowCardScoreDisplay
        isLoading={isLoading}
        score={score}
        scoreCardId={scoreCardId}
        sessionId={sessionId}
        sharedWithManager={sharedWithManager}
        userId={userId}
        variant={scoreCardVariant}
      />
      <div className={scoreRowCardStyles.content}>
        <ScoreRowCardTitleRowAndSkillTag
          category={category}
          isLoading={isLoading}
          showSkillLevelTag={showSkillLevelTag}
          skillLevel={skillLevel}
        />
        <div
          className={scoreRowCardStyles.feedbackTransitionWrapper}
          data-testid="score-row-card-feedback-wrapper"
          onTransitionEnd={handleFeedbackTransitionEnd}
          style={typeof feedbackHeight === 'number' ? { height: `${feedbackHeight}px` } : undefined}
        >
          <div
            className={cn(scoreRowCardStyles.feedbackTransitionInner, {
              'opacity-95': isAnimatingFeedback,
            })}
            ref={feedbackContentRef}
          >
            <ScoreRowCardFeedbackSection
              feedback={feedback}
              isLoading={isLoading}
              type={type}
              visibleFeedback={visibleFeedback}
            />
          </div>
        </div>
        <ScoreRowCardToggleButton
          collapseLabel={collapseLabel}
          expandLabel={expandLabel}
          forceExpanded={forceExpanded}
          hasMoreFeedback={hasMoreFeedback}
          isExpanded={isExpanded}
          isLoading={isLoading}
          onToggle={handleToggle}
          showToggle={showToggle}
        />
      </div>
    </div>
  );
};

ScoreRowCard.displayName = 'ScoreRowCard';

export default ScoreRowCard;
